import { useEffect, useState } from 'react'
import { Button } from './Button'
import { ConfirmDialog, type ConfirmQuestion } from './ConfirmDialog'

/**
 * A button that needs two presses to mean it: the first arms it, the second
 * acts. Four idle seconds, a click elsewhere or a blur stands it back down,
 * so a stray double-click can never end something on its own.
 *
 * Given an `ask`, it asks in a window instead — for the endings that cannot
 * be taken back, where the question deserves a sentence of its own.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  busy,
  disabled = false,
  onConfirm,
  ask,
  danger = false,
}: {
  label: string
  /** The armed press's words; unused when `ask` opens a window instead. */
  confirmLabel?: string
  ask?: ConfirmQuestion
  busy: boolean
  disabled?: boolean
  onConfirm: () => void
  /** An ending, worn red from the first press rather than only once armed. */
  danger?: boolean
}) {
  const [armed, setArmed] = useState(false)
  const [asking, setAsking] = useState(false)

  useEffect(() => {
    if (!armed) return
    const timer = window.setTimeout(() => setArmed(false), 4000)
    return () => window.clearTimeout(timer)
  }, [armed])

  if (ask) {
    return (
      <>
        <Button size="sm" variant={danger ? 'danger' : 'secondary'} busy={busy} disabled={disabled} onClick={() => setAsking(true)}>
          {label}
        </Button>
        <ConfirmDialog open={asking} question={ask} busy={busy} onConfirm={onConfirm} onClose={() => setAsking(false)} />
      </>
    )
  }

  return (
    <Button
      size="sm"
      variant={armed || danger ? 'danger' : 'secondary'}
      busy={busy}
      disabled={disabled}
      onBlur={() => setArmed(false)}
      onClick={() => {
        if (armed) {
          setArmed(false)
          onConfirm()
        } else {
          setArmed(true)
        }
      }}
    >
      {armed ? (confirmLabel ?? `Confirm ${label.toLowerCase()}`) : label}
    </Button>
  )
}
